import dico from "../../../util/src/language/dico.js";
import type { BehaviorDraggedNodeTemplate } from "../../../util/src/blueprint/types.js";
import type { BehaviorNode } from "../../../util/src/blueprint/index.js";
import type {
  DataSourceId,
  LayoutNode,
} from "../../../util/src/layout/types.js";
import type { DragEvent } from "react";
import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  getBehaviorTemplateCatalog,
  type BehaviorTemplateCatalogItem,
  type BehaviorTemplateCatalogSection,
} from "./BehaviorTemplateCatalog.js";
import type { BehaviorCanvasScope } from "./BehaviorCanvasScope.js";
import { matchesBehaviorCatalogSearch } from "./behaviorCatalogSearch.js";

export const BEHAVIOR_NODE_DRAG_MIME =
  "application/x-meet-manager-blueprint-node";

const SECTION_ORDER: BehaviorTemplateCatalogSection[] = [
  "events",
  "variables",
  "controls",
  "actions",
  "operators",
];

// Re-export so consumers can stay on one import path.
export type { BehaviorDraggedNodeTemplate };

// ── Shared styles ─────────────────────────────────────────────────────────────

const NODE_BUTTON_SX = {
  display: "block",
  width: "100%",
  border: "1px solid transparent",
  borderRadius: 1,
  py: 0.75,
  px: 1,
  ml: 1,
  backgroundColor: "transparent",
  boxSizing: "border-box",
  textAlign: "left",
  fontSize: 13,
  lineHeight: 1.4,
  color: "text.primary",
  cursor: "grab",
  "&:active": { cursor: "grabbing" },
  "&:hover": { backgroundColor: "action.hover" },
} as const;

const TYPE_DOT_STYLE = {
  display: "inline-block",
  width: 8,
  height: 8,
  borderRadius: 99,
  marginRight: 8,
} as const;

// ── Sub-components ────────────────────────────────────────────────────────────

const SubCategoryLabel = ({ label }: { label: string }) => (
  <Typography
    variant="caption"
    sx={{
      mb: 0.5,
      px: 1,
      display: "block",
      fontWeight: 600,
      color: "text.secondary",
    }}
  >
    {label}
  </Typography>
);

const SectionAccordion = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Accordion
    defaultExpanded
    disableGutters
    elevation={0}
    sx={{ backgroundColor: "transparent", "&:before": { display: "none" } }}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      sx={{ px: 1, minHeight: "32px !important" }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 600 }}
      >
        {title}
      </Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ p: 0, pt: 0.25 }}>{children}</AccordionDetails>
  </Accordion>
);

interface NodeListItemProps {
  label: string;
  dotColor: string;
  onDragStart: (event: DragEvent<HTMLButtonElement>) => void;
}

/** A single draggable item in the node palette. */
const NodeListItem = ({ label, dotColor, onDragStart }: NodeListItemProps) => (
  <Box
    component="button"
    type="button"
    draggable
    onDragStart={onDragStart}
    sx={NODE_BUTTON_SX}
  >
    <span style={{ ...TYPE_DOT_STYLE, backgroundColor: dotColor }} />
    {label}
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────

interface BehaviorNodeListProps {
  node: LayoutNode;
  scope?: BehaviorCanvasScope;
  allowRowIndexVariable?: boolean;
  layoutListBindingSource?: DataSourceId;
  readOnly?: boolean;
  sceneGraphNodes?: BehaviorNode[];
}

type CatalogGroup = {
  key: string;
  subCategoryLabel?: string;
  items: BehaviorTemplateCatalogItem[];
};

type CatalogSection = {
  key: BehaviorTemplateCatalogSection;
  title: string;
  groups: CatalogGroup[];
};

const BehaviorNodeListComponent = ({
  node,
  scope = "node",
  allowRowIndexVariable = false,
  layoutListBindingSource,
  readOnly = false,
  sceneGraphNodes,
}: BehaviorNodeListProps) => {
  const bpDico = dico().behavior;
  const [searchQuery, setSearchQuery] = useState("");
  const searchPlaceholder = (bpDico.nodeList as Record<string, unknown>)
    .searchPlaceholder;
  const normalizedSearch = searchQuery.trim().toLocaleLowerCase();
  const templateCatalog = useMemo(
    () =>
      getBehaviorTemplateCatalog({
        node,
        scope,
        allowRowIndexVariable,
        layoutListBindingSource,
        connectableOnly: false,
        sceneGraphNodes,
      }),
    [
      allowRowIndexVariable,
      layoutListBindingSource,
      node,
      scope,
      sceneGraphNodes,
    ],
  );
  const filteredCatalog = useMemo(
    () =>
      normalizedSearch
        ? templateCatalog.filter((item) =>
            matchesBehaviorCatalogSearch(item, normalizedSearch),
          )
        : templateCatalog,
    [normalizedSearch, templateCatalog],
  );
  const sections = useMemo<CatalogSection[]>(() => {
    const sectionMap = new Map<
      BehaviorTemplateCatalogSection,
      {
        title: string;
        groups: Map<string, CatalogGroup>;
      }
    >();

    filteredCatalog.forEach((item) => {
      const existingSection = sectionMap.get(item.section) ?? {
        title: item.categoryLabel,
        groups: new Map<string, CatalogGroup>(),
      };
      if (!sectionMap.has(item.section)) {
        sectionMap.set(item.section, existingSection);
      }
      const groupKey = `${item.categoryLabel}::${item.subCategoryLabel ?? ""}`;
      const existingGroup = existingSection.groups.get(groupKey) ?? {
        key: groupKey,
        subCategoryLabel: item.subCategoryLabel,
        items: [],
      };
      if (!existingSection.groups.has(groupKey)) {
        existingSection.groups.set(groupKey, existingGroup);
      }
      existingGroup.items.push(item);
    });

    return SECTION_ORDER.flatMap((sectionKey) => {
      const section = sectionMap.get(sectionKey);
      if (!section) {
        return [];
      }
      return [
        {
          key: sectionKey,
          title: section.title,
          groups: Array.from(section.groups.values()),
        },
      ];
    });
  }, [filteredCatalog]);

  const startDrag = (
    event: DragEvent<HTMLButtonElement>,
    template: BehaviorDraggedNodeTemplate,
  ) => {
    const payload = JSON.stringify(template);
    event.dataTransfer.setData(BEHAVIOR_NODE_DRAG_MIME, payload);
    event.dataTransfer.setData("text/plain", payload);
    event.dataTransfer.effectAllowed = "copy";
  };

  return (
    <Box
      data-behavior-palette
      sx={{
        width: 240,
        minWidth: 240,
        maxWidth: 240,
        position: "relative",
        zIndex: 2,
        borderRight: "1px solid #e0e0e0",
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        height: "100%",
        boxSizing: "border-box",
        alignSelf: "stretch",
        ...(readOnly ? { pointerEvents: "none", opacity: 0.5 } : {}),
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: 1,
          pt: 1,
          pb: 1,
          backgroundColor: "#fff",
          borderBottom: "1px solid #eee",
          boxSizing: "border-box",
          width: "100%",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ px: 1, pb: 1, fontWeight: 600, color: "text.secondary" }}
        >
          {bpDico.nodeList.title}
        </Typography>
        <Box sx={{ px: 1, width: "100%", boxSizing: "border-box" }}>
          <TextField
            size="small"
            fullWidth
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={
              typeof searchPlaceholder === "string"
                ? searchPlaceholder
                : bpDico.nodeList.title
            }
          />
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          minHeight: 0,
          p: 1,
        }}
      >
        {sections.map((section) => (
          <SectionAccordion key={section.key} title={section.title}>
            <Stack spacing={1}>
              {section.groups.map((group) => (
                <Box key={group.key}>
                  {group.subCategoryLabel ? (
                    <SubCategoryLabel label={group.subCategoryLabel} />
                  ) : null}
                  <Stack spacing={0.6}>
                    {group.items.map((item) => (
                      <NodeListItem
                        key={item.id}
                        label={item.label}
                        dotColor={item.dotColor}
                        onDragStart={(event) => startDrag(event, item.template)}
                      />
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </SectionAccordion>
        ))}
      </Box>
    </Box>
  );
};

export const BehaviorNodeList = React.memo(BehaviorNodeListComponent);
